/**
 * Cash Balances Report.
 * @module advisor/components/reports/CashBalancesReport
 *
 */

import React, {useEffect, useState} from 'react';
import debug from 'debug';
import {connect} from 'react-redux';
import axiosInstance from '../../../shared/utils/axios';
import {_openModal} from '../../../shared/reducers/modals';
import moment from 'moment';
import lazy from 'lazy.js';
import {_getUnreadReports, isEntitled} from '../../../shared/store';
import PropTypes from 'prop-types';
import {DatePicker, DescriptiveText, LoadingSpinner, RadioSet, SectionTitle, Steps} from '../../../shared';

const log = debug('circleblack:report:CashBalancesReport');

/**
 * Cash Balances Report component.
 *
 * @name CashBalancesReport
 * @requires module:shared/components/Button
 * @requires module:shared/components/SectionTitle
 *
 */
const CashBalancesReport = (props) => {
  const {report, session, cancelAction, unreadReportsTimeoutID, unreadReports, reportPageVisitTimestamp, getUnreadReports} = props;
  const today = moment().format('YYYY-MM-DD');
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [periodLabel, setPeriodLabel] = useState('');

  const setupParams = () => {
    log('setupParams');
    return lazy(report.parameters)
      .reject({name: 'format'})
      .map((param) => {
        if (param.default && (!param.value)) {
          param.value = param.default;
        }
        if (param.name === 'date') {
          param.value = '';
        }
        if (param.name === 'firmID') {
          param.value = session.profile.firm.id;
        }
        return param;
      })
      .indexBy('name')
      .toObject();
  };

  const [params, setParams] = useState(setupParams(report));

  const canGenerate = (parameters) => parameters.date.value;

  const generateReport = () => {
    if (!isEntitled([{group: 'compliance', feature: 'core', access: 'r'}])) {
      return;
    }
    return axiosInstance.post(
      '/reports/' + report.name,
      {parameters: Object.values(params).map((param) => ({id: param.id, value: param.value}))}
    )
      .then(() => {
        setSuccessMessage('The report you requested is generating. You may view it shortly.');
        setStep('END');

        getUnreadReports(unreadReportsTimeoutID, unreadReports, reportPageVisitTimestamp, 30000);
      })
      .catch(err => {
        const title = err.response && err.response.data && err.response.data.error && err.response.data.error.title !== undefined
          ? err.response.data.error.title
          : 'Report Couldn\'t be created';
        const body = (err.response && err.response.data && err.response.data.error && err.response.data.error.msg) || err.message;
        setErrorMessage(title + ': ' + body);
      });
  };

  useEffect(() => {
    if (Number(params.period.value) === 1) {
      setPeriodLabel('As Of Date');
    } else {
      setPeriodLabel('Week Ending');
    }
    if (loading && params) {
      setLoading(false);
    }
  }, [params.period.value]);

  const renderContent = () => <div>
    <div>
      <SectionTitle title={'Dates'}/>
      <RadioSet dataPoint={{name: 'period', value: params.period.value}}
                onChange={(name, value) => {
                  setParams({...params, period: {...params.period, value: value}});
                }}
                options={[
                  {label: 'Daily', value: 1},
                  {label: 'Weekly', value: 7},
                ]}
                orientation={'row'}
                optionWidth={'100px'}/>
      <DatePicker title={periodLabel}
                  width={'150px'}
                  layout={{grow: 1}}
                  dataPoint={{name: 'date', value: params.date.value}}
                  onChange={(name, value) => {
                    setParams({...params, date: {...params.date, value: value}});
                  }}
                  maxDate={today}/>
    </div>
  </div>;

  if (loading) {
    return <LoadingSpinner/>;
  }

  return <Steps
    height={'100%'}
    completedStateContent={<DescriptiveText>{successMessage || ''}</DescriptiveText>}
    steps={[
      {
        title: 'Generate Cash Balances - Select Parameters',
        content: renderContent,
        next: {
          label: 'Generate',
          action: generateReport,
          disabled: !canGenerate(params),
        },
      },
    ].filter(s => !!s)}
    stepIndex={step}
    canStepBack={false}
    cancelAction={cancelAction}
    errorMessage={errorMessage}/>;
};

const mapStateToProps = state => ({
  session: state.session,
  unreadReportsTimeoutID: state.badges.unreadReportsTimeoutID,
  unreadReports: state.badges.unreadReports,
  reportPageVisitTimestamp: state.badges.reportPageVisitTimestamp,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  getUnreadReports: (timeoutID, unread, timestamp, interval) => dispatch(_getUnreadReports(timeoutID, unread, timestamp, interval)),
});

CashBalancesReport.propTypes = {
  /**
   * The Report to be generated
   */
  report: PropTypes.shape({
    name: PropTypes.string,
    parameters: PropTypes.array,
  }).isRequired,
  /**
   * Is from Models Reducer
   */
  openModal: PropTypes.func.isRequired,
  /**
   * To reset the Report generate tile to default state
   */
  cancelAction: PropTypes.func.isRequired,
  /**
   /**
   * Is Session Reducer
   */
  session: PropTypes.shape({
    profile: PropTypes.shape({
      firm: PropTypes.shape({
        id: PropTypes.number.isRequired,
      }),
    }),
  }).isRequired,
  /**
   * Are from badges reducer
   */
  getUnreadReports: PropTypes.func,
  unreadReportsTimeoutID: PropTypes.object,
  unreadReports: PropTypes.object,
  reportPageVisitTimestamp: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(CashBalancesReport);
